<template>
    <Card @click="allowSelection && $emit('select', chore, !selected)"
        class="relative transition-all cursor-pointer hover:shadow-md !border-1" :class="cardClasses">

        <template #content>
            <!-- Selection indicator -->
            <div class="absolute top-3 right-3">
                <i class="pi" :class="selected ? 'pi-check-circle text-primary' : 'pi-circle text-gray-400'" />
            </div>

            <div class="space-y-3">
                <!-- Header with name and XP -->
                <div class="space-y-1">
                    <h3 class="font-semibold text-lg text-gray-900 dark:text-gray-100">
                        {{ chore.name }}
                    </h3>
                    <div class="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <span class="flex items-center gap-1">
                            <i class="pi pi-clock text-xs"></i>
                            {{ chore.estimatedMinutes }} min
                        </span>
                        <span class="flex items-center gap-1 font-medium text-primary">
                            <i class="pi pi-star text-xs"></i>
                            {{ totalXp }} XP
                        </span>
                    </div>
                </div>

                <!-- Due date status -->
                <div v-if="dueText" class="flex items-center justify-between">
                    <span class="text-sm font-medium" :class="dueTextClasses">
                        {{ dueText }}
                    </span>
                    <Button v-if="!chore.autoSchedule && allowEdit" icon="pi pi-times" severity="danger" text
                        size="small" :loading="removingDueDate" @click.stop="removeDueDate()"
                        v-tooltip="$t('removeDueDate')" />
                </div>

                <!-- Assignee (simplified) -->
                <div v-if="chore.assignedUserName && !chore.isAdHocTask"
                    class="text-sm text-gray-600 dark:text-gray-400">
                    <span class="flex items-center gap-1">
                        <i class="pi pi-user text-xs"></i>
                        {{ chore.assignedUserName }}
                    </span>
                </div>

                <!-- Last performed (minimal) -->
                <div v-if="chore.lastPerformedAt" class="text-xs text-gray-500 dark:text-gray-500">
                    {{ $t('lastCompleted') }}: <span v-if="!isToday(chore.lastPerformedAt)">{{ d(new
                        Date(chore.lastPerformedAt), 'short') }}</span><span v-else
                        class="font-semibold text-primary">{{ $t('today') }}</span>
                </div>

                <!-- Expandable details (integrated) -->
                <div v-if="showDetails" class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div class="space-y-3 text-sm">
                        <div v-if="chore.description" class="text-gray-700 dark:text-gray-300">
                            <p class="font-medium mb-1">{{ $t('description') }}:</p>
                            <p>{{ chore.description }}</p>
                        </div>

                        <div class="flex items-center gap-4 text-gray-600 dark:text-gray-400">
                            <span>{{ $t('category') }}: {{ chore.categoryName }}</span>
                            <span>{{ $t('difficulty') }}: {{ $t("difficulty." + chore.difficultyLevel) }}</span>
                        </div>

                        <div v-if="chore.appliedBonuses && chore.appliedBonuses.length > 0">
                            <p class="font-medium mb-2 text-gray-700 dark:text-gray-300">{{ $t('activeBonuses') }}:</p>
                            <div class="space-y-2">
                                <div v-for="bonus in chore.appliedBonuses" :key="bonus.type!"
                                    class="flex justify-between items-start gap-3">
                                    <div class="flex-1">
                                        <div class="font-medium text-sm">{{ $t(`bonusTypes.${bonus.type}`) }}</div>
                                        <div class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            {{ $t(`bonusDescriptions.${bonus.type}`) }}
                                        </div>
                                    </div>
                                    <Tag :value="`+${bonus.xp} XP`" severity="success" size="small" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <!-- Footer with actions -->
        <template #footer>
            <div class="flex items-center justify-between">
                <div class="flex gap-1">
                    <Button v-if="!chore.isAdHocTask && allowEdit" icon="pi pi-user" severity="secondary" text
                        size="small" @click.stop="showAssigneeDialog = true" v-tooltip="$t('changeAssignee')" />

                    <Button :icon="showDetails ? 'pi pi-chevron-up' : 'pi pi-info-circle'" text size="small"
                        @click.stop="showDetails = !showDetails"
                        v-tooltip="showDetails ? $t('lessDetails') : $t('moreDetails')" />
                </div>

                <div v-if="chore.bonusXp && chore.bonusXp > 0"
                    class="text-xs text-green-600 dark:text-green-400 font-medium">
                    +{{ chore.bonusXp }} {{ $t('bonus') }} XP
                </div>
            </div>
        </template>
    </Card>

    <!-- Assignee Change Dialog -->
    <Dialog v-model:visible="showAssigneeDialog" :header="$t('changeAssignee')" modal class="w-96"
        @show="onShowAssigneeDialog">
        <div class="space-y-4">
            <div>
                <label class="block text-sm font-medium mb-2">{{ $t('selectNewAssignee') }}</label>
                <Select v-model="selectedAssignee" :options="availableUsers" optionLabel="username" optionValue="id"
                    filter fluid showClear :placeholder="$t('chooseUser')" />
            </div>
        </div>
        <template #footer>
            <Button :label="$t('cancel')" text @click="showAssigneeDialog = false" />
            <Button :label="$t('update')" @click="updateAssignee" :loading="settingAssignee" />
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { type ChoreTileDto, type UserDto } from "@/client";
import { computed, ref } from 'vue';
import { useI18n } from "vue-i18n";
import { useTaskBoardStore } from "@/stores";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";

const toast = useToast();
const confirm = useConfirm();

const { updateChoreInstance } = useTaskBoardStore();

const { d, t } = useI18n();

const props = defineProps<{
    chore: ChoreTileDto;
    selected?: boolean;
    allowSelection?: boolean;
    allowEdit?: boolean;
    availableUsers?: UserDto[];
}>();

const emit = defineEmits<{
    (e: 'select', chore: ChoreTileDto, selected: boolean): void;
}>();

// Map recurrence intervals to approximate days
const recurrenceIntervalDays: Record<string, number> = {
    Daily: 1,
    TwiceWeekly: 3.5,
    Weekly: 7,
    BiWeekly: 14,
    Monthly: 30,
    BiMonthly: 60,
    Yearly: 365
};

// Urgency thresholds as percentage of recurrence interval
// 'approaching' when less than 30% of interval remains
// 'urgent' when less than 15% of interval remains
const APPROACHING_THRESHOLD = 0.30;
const URGENT_THRESHOLD = 0.15;

// Urgency levels: 'none' | 'approaching' | 'urgent' | 'overdue'
type UrgencyLevel = 'none' | 'approaching' | 'urgent' | 'overdue';

// Computed properties
const totalXp = computed(() => (props.chore.baseXp || 0) + (props.chore.bonusXp || 0));

const today = computed(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
});

const dueDate = computed(() => {
    if (!props.chore.dueAt) return null;
    // props.chore.dueAt is in UTC. We will treat it as local timezone making 23:59:59 UTC => 23:59:59 local time.
    console.log(props.chore.dueAt)
    return new Date(
        props.chore.dueAt.getUTCFullYear(),
        props.chore.dueAt.getUTCMonth(),
        props.chore.dueAt.getUTCDate(),
        props.chore.dueAt.getUTCHours(),
        props.chore.dueAt.getUTCMinutes(),
        props.chore.dueAt.getUTCSeconds());
});

const daysUntilDue = computed(() => {
    if (!dueDate.value) return null;
    return Math.floor((dueDate.value.getTime() - today.value.getTime()) / (1000 * 60 * 60 * 24));
});

const isDue = computed(() => !!props.chore.dueAt);

const isOverdue = computed(() => {
    if (daysUntilDue.value === null) return false;
    return daysUntilDue.value < 0;
});

const urgencyLevel = computed<UrgencyLevel>(() => {
    if (!isDue.value || daysUntilDue.value === null) return 'none';
    if (isOverdue.value) return 'overdue';

    const interval = props.chore.recurrenceInterval;
    const intervalDays = interval ? recurrenceIntervalDays[interval] : null;

    // If no recurrence interval (one-time or ad-hoc task), use simple day-based thresholds
    if (!intervalDays) {
        if (daysUntilDue.value <= 1) return 'urgent';
        if (daysUntilDue.value <= 3) return 'approaching';
        return 'none';
    }

    // Calculate urgency based on remaining time relative to interval
    const remainingRatio = daysUntilDue.value / intervalDays;

    if (remainingRatio <= URGENT_THRESHOLD) return 'urgent';
    if (remainingRatio <= APPROACHING_THRESHOLD) return 'approaching';
    return 'none';
});

const cardClasses = computed(() => {
    if (props.selected) {
        return 'border-primary !ring-2 ring-primary/20 !bg-primary/5';
    }

    switch (urgencyLevel.value) {
        case 'overdue':
            return 'border-red-300 !bg-red-50 dark:border-red-700 dark:!bg-red-900/20';
        case 'urgent':
            return 'border-orange-300 !bg-orange-50 dark:border-orange-700 dark:!bg-orange-900/15';
        case 'approaching':
            return 'border-yellow-300 !bg-yellow-50 dark:border-yellow-700 dark:!bg-yellow-900/10';
        default:
            return 'border-gray-200 dark:border-gray-700 !hover:border-gray-300 dark:hover:!border-gray-600';
    }
});

const dueTextClasses = computed(() => {
    switch (urgencyLevel.value) {
        case 'overdue':
            return 'text-red-600 dark:text-red-400';
        case 'urgent':
            return 'text-orange-600 dark:text-orange-400';
        case 'approaching':
            return 'text-yellow-600 dark:text-yellow-500';
        default:
            return 'text-gray-600 dark:text-gray-400';
    }
});

function isToday(date: Date) {
    const d = new Date(date);
    return d.getDate() === today.value.getDate() &&
        d.getMonth() === today.value.getMonth() &&
        d.getFullYear() === today.value.getFullYear();
}

const dueText = computed(() => {
    if (!dueDate.value || daysUntilDue.value === null) return null;

    const diffDays = daysUntilDue.value;
    if (diffDays < 0) {
        return diffDays === -1 ? t('overdue.yesterday') : t('overdue.days', { days: Math.abs(diffDays) });
    } else if (diffDays === 0) {
        return t('due.today');
    } else if (diffDays === 1) {
        return t('due.tomorrow');
    } else {
        return t('due.inDays', { days: diffDays });
    }
});

// Local state
const showDetails = ref(false);
const showAssigneeDialog = ref(false);
const selectedAssignee = ref<string | null | undefined>();
const settingAssignee = ref(false);
const removingDueDate = ref(false);

// Methods
const onShowAssigneeDialog = () => {
    if (props.chore.assignedUserName) {
        selectedAssignee.value = props.availableUsers?.find(u => u.username === props.chore.assignedUserName)?.id;
    } else {
        selectedAssignee.value = undefined;
    }
};

const updateAssignee = async () => {
    settingAssignee.value = true;
    try {
        await updateChoreInstance(props.chore, selectedAssignee.value, props.chore.dueAt);
        const assignedUserName = props.availableUsers?.find(u => u.id === selectedAssignee.value)?.username;
        if (selectedAssignee.value) {
            toast.add({
                severity: 'success',
                summary: t('assigneeUpdated'),
                detail: t('assigneeUpdatedDetail', { user: assignedUserName }),
                life: 3000
            });
        } else {
            toast.add({
                severity: 'info',
                summary: t('assigneeUpdated'),
                detail: t('assigneeRemoved'),
                life: 3000
            });
        }
        showAssigneeDialog.value = false;
        selectedAssignee.value = undefined;
    } catch (error) {
        console.error('Error updating assignee:', error);
        toast.add({
            severity: 'error',
            summary: t('error'),
            detail: t('errorUpdatingAssigneeDetail'),
            life: 5000
        });
    } finally {
        settingAssignee.value = false;
    }
};

const removeDueDate = () => {
    console.log("Removing due date for chore:", props.chore);
    confirm.require({
        header: t('confirmRemoveDueDateHeader'),
        message: t('confirmRemoveDueDate'),
        rejectProps: {
            label: t('cancel'),
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: t('yes')
        },
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
            removingDueDate.value = true;
            try {
                const userId = props.availableUsers?.find(u => u.username === props.chore.assignedUserName)?.id;
                await updateChoreInstance(props.chore, userId, null);
                toast.add({
                    severity: 'info',
                    summary: t('removedDueDate'),
                    detail: t('removedDueDateDetail'),
                    life: 3000
                });
            } catch (error) {
                console.error('Error removing due date:', error);
                toast.add({
                    severity: 'error',
                    summary: t('error'),
                    detail: t('errorRemovingDueDateDetail'),
                    life: 5000
                });
            } finally {
                removingDueDate.value = false;
            }
        }
    });
};
</script>